// app/javascript/controllers/cloudflare_video_upload_controller.js

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["dropArea", "progressBar", "statusBar", "error", "success"]

    static values = {
        createUrl: String,
        finalizeUrl: String,
        trackingToken: String,
        email: String,
        parentName: String,
        childName: String,
        charityName: String,
    }

    async connect() {
        const connection = this.connection = Symbol("video-uploader")
        try {
            const library = await this.loadUploadLibrary()
            // Turbo may remove/reconnect this form while the library is loading.
            if (this.connection !== connection || !this.element.isConnected) return
            this.initializeUploader(library)
        } catch (error) {
            if (this.connection !== connection || !this.element.isConnected) return
            this.disposeUploader()
            console.error("Could not load the video uploader", error)
            this.showError()
        }
    }

    loadUploadLibrary() {
        // A blocked upload CDN must not prevent the entire application (including
        // charity verification) from starting. Keep the existing pinned version.
        return import("https://releases.transloadit.com/uppy/v3.26.0/uppy.min.mjs")
    }

    initializeUploader({ Uppy, Tus, DragDrop, ProgressBar, StatusBar }) {
        this.uppy = new Uppy({
            debug: false,
            autoProceed: true,
            restrictions: {
                maxNumberOfFiles: 1,
                allowedFileTypes: ["video/*"],
            },
            meta: {
                tracking_token: this.trackingTokenValue || "",
                email: this.emailValue || "",
                parent_name: this.parentNameValue || "",
                child_name: this.childNameValue || "",
                charity_name: this.charityNameValue || "",
            },
        })

        this.uppy
            .use(DragDrop, {
                target: this.dropAreaTarget,
                note: "Upload one short video file",
                locale: {
                    strings: {
                        dropHereOr: "Drop your video here or %{browse}",
                        browse: "choose a file",
                    },
                },
            })
            .use(Tus, {
                endpoint: this.createUrlValue,
                chunkSize: 150 * 1024 * 1024,
                retryDelays: [0, 1000, 3000, 5000],
            })
            .use(ProgressBar, {
                target: this.progressBarTarget,
                hideAfterFinish: false,
            })
            .use(StatusBar, {
                target: this.statusBarTarget,
                showProgressDetails: true,
                hidePauseResumeButton: true,
                hideCancelButton: true,
                hideAfterFinish: false,
                locale: {
                    strings: {
                        complete: "We have successfully received your video. Thank you!",
                    },
                },
            })

        this.uppy.on("upload-success", (file, response) => {
            this.hideError()
            this.showSuccess()
            this.finalizeUpload(file, response)
        })

        this.uppy.on("error", (error) => {
            console.error(error)
            this.showError()
        })

        this.uppy.on("upload-error", (file, error) => {
            console.error(file, error)
            this.showError()
        })
    }

    disconnect() {
        this.connection = null
        this.disposeUploader()
    }

    disposeUploader() {
        if (this.uppy) {
            // Support the pinned library's cleanup API and newer Uppy versions.
            if (typeof this.uppy.destroy === "function") this.uppy.destroy()
            else this.uppy.close()
            this.uppy = null
        }
    }

    finalizeUpload(file, response) {
        const uploadUrl = response.uploadURL || ""
        const videoId = uploadUrl.split("/").filter(Boolean).pop()

        if (!videoId) return

        fetch(this.finalizeUrlValue, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": this.csrfToken(),
            },
            body: JSON.stringify({
                video_id: videoId,
                upload_url: uploadUrl,
                file_name: file.name,
                file_type: file.type,
                file_size: file.size,
            }),
        }).catch((error) => {
            console.error("Could not finalize video upload", error)
        })
    }

    showError() {
        this.errorTarget.classList.remove("d-none")
        this.successTarget.classList.add("d-none")
    }

    hideError() {
        this.errorTarget.classList.add("d-none")
    }

    showSuccess() {
        this.successTarget.classList.remove("d-none")
    }

    csrfToken() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }
}
